<template>
  <RevLoadingScreen
    v-if="isLoading && !cart"
    class="bg-surface-default-mid fixed inset-0"
    data-qa="loading-screen"
    :text="''"
  />
  <slot
    v-if="cart"
    :addressList="cart.addressList"
    :billableCountries="cart.billableCountries"
    :isExpressCheckoutEligible="cart.isExpressCheckoutEligible"
    :items="cart.cartItems"
    :lastCollectionPoint="cart.lastCollectionPoint"
    :loading="isLoading"
    :shippableCountries="cart.shippableCountries"
    :summary="cart.summary"
    :userInformation="cart.userInformation"
    @update="handleUpdate"
    @updating="handleUpdating"
  />
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'

import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useBuybackOffer'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { camelizeKeys } from '@backmarket/utils/object/camelizeKeys'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import type { CartItem } from '@backmarket/nuxt-layer-checkout/api/cart/types'
import { useCheckoutTracking } from '@backmarket/nuxt-layer-checkout/composables/useCheckoutTracking'
import { useDiscountStore } from '@backmarket/nuxt-layer-checkout/stores/discountStore'

// default: undefined prevents Vue's boolean casting (absent boolean props default to false otherwise)
const props = withDefaults(defineProps<{ newPriceGrid?: boolean }>(), {
  newPriceGrid: undefined,
})

const $httpFetch = useHttpFetch()
const experiments = useExperiments()
const { setCartPriceWithSwapDiscount } = useBuybackOffer()
const { trackFunnel } = useCheckoutTracking()
const discountStore = useDiscountStore()
const { countryCode } = useMarketplace().market

function getStoredPostalCode() {
  return localStorage.getItem(`bm.deliveryZipCode.${countryCode}`)
}

// Callers can force a fixed value; falls back to the experiment flag.
const isExperimentNewPriceGrid =
  experiments['experiment.serviceFeeGrid'] === 'newServiceFeeGrid'

const newPriceGrid = props.newPriceGrid ?? isExperimentNewPriceGrid

type Cart = ApiSpecs['/bm/cart']['GET']['response']

const isLoading = ref(false)
const cart = ref<Cart | null>(null)

// To be removed with the Promo code revamp - CK-6662
watch(
  () => cart.value?.summary?.totalPrice,
  (newPrice, oldPrice) => {
    if (newPrice !== oldPrice && discountStore.isApplied) {
      discountStore.reset()
    }
  },
)

async function fetchCart() {
  try {
    const storedPostalCode = getStoredPostalCode()
    const response = await $httpFetch('/bm/cart', {
      query: {
        newPriceGrid,
        insuranceOfferFilters: [experiments['experiment.annualRecurring']],
        ...(storedPostalCode && { postalCode: storedPostalCode }),
      },
    })
    const responseCamelized = camelizeKeys(response)
    setCartPriceWithSwapDiscount(
      responseCamelized.swap?.ad?.cartPriceWithSwapDiscount ?? null,
    )

    return responseCamelized
  } catch (error) {
    return null
  }
}

onMounted(async () => {
  isLoading.value = true
  cart.value = await fetchCart()
  isLoading.value = false
})

/**
 * Applies an optimistic update to the cart items by replacing the item with matching listingId
 * @param currentCartItems - The current array of cart items
 * @param itemToUpdate - The updated cart item to replace in the array
 * @returns A new array with the updated item replaced
 */
function applyOptimisticUpdate(
  cartItems: CartItem[],
  updatedItem: CartItem,
): CartItem[] {
  return cartItems.reduce<CartItem[]>((items, item) => {
    if (item.listingId === updatedItem.listingId) {
      return [...items, updatedItem]
    }

    return [...items, item]
  }, [])
}

async function handleUpdating(updatedItem?: CartItem) {
  const hasOptimisticUpdate = !!updatedItem

  // Set loading state only if we're not doing optimistic updates
  if (!hasOptimisticUpdate) {
    isLoading.value = true
  }

  // Apply optimistic update to immediately reflect changes in the UI
  if (hasOptimisticUpdate && cart.value && updatedItem) {
    cart.value.cartItems = applyOptimisticUpdate(
      cart.value.cartItems,
      updatedItem,
    )
  }
}

async function handleUpdate(callback?: () => void) {
  isLoading.value = true

  try {
    const updatedCart = await fetchCart()

    if (updatedCart) {
      cart.value = updatedCart
      trackFunnel(updatedCart.cartItems, updatedCart.swap.offer)
    }

    if (callback) {
      callback()
    }
  } finally {
    isLoading.value = false
  }
}
</script>
